import {Component, ViewEncapsulation} from '@angular/core';
import {DialogFormInput, FormService, LangService, LoaderService, NavigateService, ShortcutAction} from 'ngx-satoris';
import {ApiService} from '../../../shared/services/api.service';
import {PassportType} from '../../../shared/models/request';
import {CartService} from '../../../shared/services/cart.service';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {RequestService} from '../../../shared/services/request.service';
import {InactivityService} from '../../../shared/services/inactivity.service';
import {ScanDocumentService} from '../../../shared/services/scan-document.service';
import {UserPermission} from '../../../shared/models/user';
import {Direction, VisaFN} from '../../../shared/models/forms';
import {ActivatedRoute} from '@angular/router';
import {canAccessWebKiosk, isCanHaveOnArrival, setCurrentCountryCat} from 'src/app/shared/models/default-filter-strategy';

@Component({
  selector: 'app-3.passport-type',
  templateUrl: './passport-type.component.html',
  styleUrl: './passport-type.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class PassportTypeComponent {
  selected: PassportType = this.cart.currentRequest?.passportType || undefined;
  subscription;
  nationalities = this.request.getTranslatedNationalities(true, this.cart.currentApplication.isLandingVisa);
  form: FormGroup;
  keepScanned = false;
  submitted: boolean;
  documentTypes: PassportType[];
  lock = false;
  paramDirection: Direction;
  shortcuts = [
    ShortcutAction.ONE,
    ShortcutAction.TWO,
    ShortcutAction.THREE,
    ShortcutAction.FOUR,
    ShortcutAction.FIVE,
    ShortcutAction.SIX,
    ShortcutAction.SEVEN,
    ShortcutAction.EIGHT,
    ShortcutAction.NINE
  ];
  nationalityFromScan: string;

  constructor(public forms: FormService,
    public nav: NavigateService,
    public api: ApiService,
    public cart: CartService,
    private request: RequestService,
    private inactivity: InactivityService,
    private loader: LoaderService,
    public scanDoc: ScanDocumentService,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private lang: LangService) {
    this.route.queryParams.subscribe(params => {
      if(params && params.direction) this.paramDirection = params.direction;
    });
    const webKiosk = sessionStorage.getItem('webKiosk') === 'true';
    this.subscription = this.cart.currentApplication$.subscribe(() => {
      if(!this.cart.currentRequest) {
        if(this.cart.currentApplication?.isRevision || (this.api.userRole.isWorker && this.api.hasPerm(UserPermission.ALLOW_CREATE) && this.api.hasPerm(UserPermission.ALLOW_CONSUME))) {
          this.nav.to('dashboard');
        } else if(this.api.userRole.isKiosk && !webKiosk) {
          this.nav.to('standby-kiosk');
        } else if(webKiosk) {
          return;
        } else if(this.api.env.formHasTravelNameStep) {
          this.nav.to('travel-name');
        } else if(this.api.env.hasMultiPersonView) {
          this.nav.to('participants');
        } else {
          this.nav.to('dashboard');
        }
      }
      this.cart.currentRequest = {...this.cart.currentRequest, latestRouteVisited: 'passport-type'};
      this.cart.saveCurrentApplication(false);
      this.selected = this.cart.currentRequest.passportType || undefined;
      this.setForm();
    });
    if(!this.inactivity.isWatching && this.api.userRole.isKiosk) this.inactivity.startWatching();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    if(!this.keepScanned) {
      this.scanDoc.scanned = false;
    }
  }

  focusSelect(passportType: PassportType) {
    if(this.cart.isFnInRevision(VisaFN.PASSPORT_TYPE, true)) return;
    if(this.selected === passportType) return;
    this.selected = passportType;
    this.cart.currentRequest = {
      ...this.cart.currentRequest,
      passportType: this.cart.currentApplication.isLandingVisa ? PassportType.ORDINARY : passportType
    };
    this.cart.saveCurrentApplication(false);
  }

  setForm() {
    this.form = this.formBuilder.group({
      nationality: ['', Validators.required]
    });
    this.nationalityFromScan = (this.scanDoc.scanned && this.nationalities?.find(nat => nat.value === this.scanDoc.scanDatas?.fields?.nationality)) ? this.scanDoc.scanDatas?.fields?.nationality : '';
    this.form.controls.nationality.patchValue(this.cart.currentRequest.nationality || this.nationalityFromScan || '');
    if(this.nationalityFromScan || this.cart.isFnInRevision(VisaFN.NATIONALITY, true)) this.form.controls.nationality.disable();
    this.setDocumentTypes();
  }

  setDocumentTypes(clear = false) {
    const nationality = this.form.controls.nationality.value;
    const isWebKiosk = sessionStorage.getItem('webKiosk') === 'true';
    const isForbiddenNationality = !canAccessWebKiosk(nationality);

    if(isWebKiosk && this.api.userRole.isKiosk && isForbiddenNationality) {
      this.documentTypes = [];
      this.loader.loading(true, {type: 'warn', custom: {innerHtml: this.lang.transform('kiosk.redirect')}}).then(() => {
        sessionStorage.removeItem('webKiosk');
        this.api.signOut('/sign-in');
      });
      return;
    }

    let docs = Object.keys(PassportType).map(key => PassportType[key as keyof typeof PassportType]);
    if(this.form.controls.nationality.value.startsWith('XX')) {
      docs = [PassportType.REFUGEE];
      this.selectPassportType(PassportType.REFUGEE, false);
    } else if(this.form.controls.nationality.value !== this.api.userInfo.server.country) {
      if(this.api.userRole.isCustomer || this.api.userRole.isKiosk){
        docs = docs.filter(doc => doc !== PassportType.IDENTITY_CARD);
      }
    } else {
      docs = docs.filter(doc => doc !== PassportType.RESIDENT_PERMIT && doc !== PassportType.PERMANENT_RESIDENT_PERMIT);
    }

    if(this.cart.currentApplication.isLandingVisa) {
      docs = docs.filter(doc => doc !== PassportType.IDENTITY_CARD && doc !== PassportType.PERMANENT_RESIDENT_PERMIT && doc !== PassportType.RESIDENT_PERMIT  && doc !== PassportType.LAISSEZ_PASSER);
    }

    this.documentTypes = docs;

    if(this.cart.currentApplication.isRevision && this.cart.isFnInRevision(VisaFN.PASSPORT_TYPE, true) && this.cart.isFnInRevision(VisaFN.NATIONALITY, true)) {
      if(this.cart.currentApplication.skipValidRevision) this.paramDirection === Direction.NEXT ? this.next() : this.previous();
    }

    if(clear) {
      this.selectPassportType(undefined);
    }

    if(!this.selected && (this.api.userRole.isWorker || this.api.userRole.isAdmin)) {
      this.selectPassportType(PassportType.ORDINARY, false);
    }
  }

  selectPassportType(passportType: PassportType, submit = true) {
    if(this.cart.isFnInRevision(VisaFN.PASSPORT_TYPE, true)) return;
    this.selected = passportType;
    this.cart.currentRequest = {
      ...this.cart.currentRequest,
      passportType: this.cart.currentApplication.isLandingVisa ? PassportType.ORDINARY: passportType
    };
    this.cart.saveCurrentApplication(false);

    if(passportType === PassportType.REFUGEE && !this.form.controls.nationality.value.toString().startsWith('XX')) {
      this.selectRefugeeNationality();
      return;
    }

    if((this.api.userRole.isWorker || this.api.userRole.isAdmin) && this.selected && this.selected === PassportType.IDENTITY_CARD && this.api.env.showBorderPassWarning !== false) {
      this.loader.loading(true, {type: 'warn', btnLabel: 'global.yes', custom: {closeBtnLabel: 'global.no', disableClose: true, innerHtml: this.lang.transform('passportType.borderPassWarning')}}).then((done: boolean) => {
        if(done) {
          this.submit();
        } else {
          this.selectPassportType(undefined, false);
        }
      });
      return;
    }

    if(this.selected && this.selected !== PassportType.ORDINARY && this.selected !== PassportType.IDENTITY_CARD && this.selected !== PassportType.RESIDENT_PERMIT) {
      this.loader.loading(true, {type: 'info', message: 'passportType.acknowledge', shortcut: {closeBtn: {action: ShortcutAction.ENTER}}}).then(() => {
        if(this.form.controls.nationality.value && submit) this.submit();
      });
    } else if(this.selected && this.form.controls.nationality.value && submit) {
      this.submit();
    }
  }

  selectRefugeeNationality(submit = true) {
    const dialogInput = [{
      formCtrlName: 'nationality',
      type: 'select',
      label: 'Nationality',
      selectOptions: this.nationalities.filter(nationality => nationality.value.toString().startsWith('XX')),
      validator: [Validators.required]
    }] as DialogFormInput[];

    const modalMessage = `${this.lang.transform('passportType.acknowledge')}<br><br>${this.lang.transform('passportType.selectRefugeeNationality')}<br>`;
    this.loader.loading(true, {type: 'info', custom: {closeBtnLabel: 'close', innerHtml: modalMessage}, btnLabel: 'global.confirm', dialogInput}).then((res: any) => {
      if(res.nationality) {
        this.form.controls.nationality.patchValue(res.nationality);
        if(submit) this.submit();
      }
    });
  }

  submit() {
    if(this.lock) return;
    this.submitted = true;
    if(this.forms.getFormErrors(this.form).length < 1) {
      if(this.api.userRole.isKiosk && ((!isCanHaveOnArrival({countryCat: setCurrentCountryCat({indexed: {Nationality: this.form.controls.nationality.value}})})
      && this.cart.currentRequest.passportType === PassportType.ORDINARY) || this.cart.currentRequest.passportType === PassportType.REFUGEE)) {
        this.lock = true;
        this.loader.loading(true, {type: 'warn', message: 'passportType.kioskWarning'}).then(() => {
          this.cart.currentRequest.nationality = undefined;
          this.setForm();
          this.lock = false;
        });
      } else {
        this.lock = false;
        this.cart.currentRequest = {
          ...this.cart.currentRequest,
          nationality: this.form.controls.nationality.value
        };
        this.cart.saveCurrentApplication(false);
        if(this.scanDoc?.scanned) {
          this.keepScanned = true;
        }
        this.next();
      }
    }
  }

  next() {
    this.nav.to('visa-type', undefined, {queryParams: {direction: Direction.NEXT}});
  }

  previous() {
    if(this.api.userRole.isCustomer && !this.cart.currentApplication?.isRevision && this.api.env.hasMultiPersonView) {
      this.nav.to('participants');
    } else if(this.cart.currentApplication?.isRevision && this.cart.currentApplication?.requestId) {
      this.nav.to('request-account/'+this.cart.currentApplication?.requestId);
    } else if(this.api.userRole.isKiosk) {
      this.nav.to('standby-desc-kiosk');
    } else {
      this.nav.to('dashboard');
    }
  }

  changeToHKG() {
    return this.loader.loading(true, {type: 'question', message: 'passportType.changeToHKG', btnLabel: 'global.yes'}).then((done: boolean) => {
      if(done) {
        this.scanDoc.changeToHKG();
        this.nationalityFromScan = 'HKG';
        this.form.controls.nationality.patchValue('HKG');
      }
    });
  }

  protected readonly ShortcutAction = ShortcutAction;
}
